'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';

import {FaEllipsisH, FaTimesCircle, FaExclamationTriangle, FaLink} from "react-icons/fa";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Accordion from 'react-bootstrap/Accordion';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import Lists from "@uw-it-sis/lib-react/lib/Lists";
import {isFalse, isEmpty, size, pluralize, capitalize, isTrue} from "@uw-it-sis/lib-react/lib/Utils";
import {handleInputMaxLength, handleInputNumbersOnly} from "@uw-it-sis/lib-react/lib/ValidationUtils";

import planItemStore from "@uw-it-sis/lib-react-myplan/lib/PlanItemStore";
import SectionMeetingDetails from '@uw-it-sis/lib-react-myplan/lib/SectionMeetingDetails';
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import RegistrationCodeLink from "@uw-it-sis/lib-react-myplan/lib/RegistrationCodeLink";
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";
import Notify from '@uw-it-sis/lib-react-myplan/lib/Notify';
import SectionCode from "@uw-it-sis/lib-react-myplan/lib/SectionCode";

import registrationDetailsStore from './RegistrationDetailsStore';
import planItemModalStore from './PlanItemModalStore';
import errorMessageFactory from './ErrorMessageFactory';
import RegistrationConstants from './RegistrationConstants';

import {PlanItemType} from "../lib/Constants";

const analyticsStore = appStore.getAnalyticsStore();

const CourseListItem = observer(({
    termId, course, courseTitleHeadings, type, isPublished, issue = false, clusterParent, isClusterParent = false, isClustered = false, isClusterParentSendToRegistration = false
}) => {
    const selectedTermData = termStore.get('publishedTerms').find(function (term) {
        return term.id === termId;
    }.bind(this));

    /**
     * Updates the store state data based on changes to the entry codes or course selection
     * @param e The onChange js event
     */
    const onChange = e => { // This is not needed now
        let value, property, identifier;
        if (e.target.type === "checkbox") {
            // this is for updating a course selector
            property = 'sendToRegistration';
            identifier = e.target.value;
            value = e.target.checked;
        } else {
            // this is for updating an entry code
            property = 'entryCode';
            identifier = e.target.dataset.activityId;
            value = e.target.value;
        }
        registrationDetailsStore.updateRegistrationItems(property, identifier, value);
    };
    /**
     * Return true if the today's date is between P1 registration date and last add date
     * @returns {boolean}
     * @private
     */
    const _isNowBetweenP1RegAndLastAddDate = () => {
        let now = moment(appStore.getNow());

        let firstRegPeriodDate = TermUtils.findPeriodOnePriorityRegDate(selectedTermData);
        firstRegPeriodDate = moment(firstRegPeriodDate.subtract(7, 'days').format('YYYY-MM-DD'));
        let lastAddDate = null;
        if (selectedTermData) {
            selectedTermData.academicCalendar.forEach( function(entry) {

                if (entry.key === 'last_add_day') {
                    // use a time of 11:59:59pm to check that today's date is past the complete day of the last add day
                    lastAddDate = moment(entry.date + " 23:59:59", "YYYY-MM-DD hh:mm:ss");
                }
            });
        }

        return now.isBetween(firstRegPeriodDate, lastAddDate, "seconds");
    };

    const _isNowAfterEighthDay = () => {
        let now = moment(appStore.getNow());
        let eighthDay = null;

        if (selectedTermData) {
            selectedTermData.academicCalendar.forEach( function(entry) {

                if (entry.key === 'FirstDay') {
                    // FirstDay is counted as day 1, so add is actually only 7 to get the 8th calendar day of term start
                    eighthDay = moment(entry.date + " 00:00:00", "YYYY-MM-DD hh:mm:ss").add(7, 'days');
                }
            });
        }

        return now.isAfter(eighthDay ,"seconds");
    };

    /**
     *  Creates the issue message component
     * @param actionCode
     * @param course
     * @param analyticsData
     * @returns {*}
     * @private
     */
    const _makeMessageComponent = (actionCode, course, analyticsData) => {
        return (
            <div key={actionCode}>
                <Lists className={_isNowBetweenP1RegAndLastAddDate() ? "text-danger" : ""}
                       data={course.validationIssues[actionCode]} unstyled/>
                {errorMessageFactory.generateLink(actionCode, course, analyticsData)}
            </div>
        );
    };

    /**
     * Adds analytics for Course Details Page (CDP) link clicks.
     *
     * @param courseCode The course code.
     * @param status The status of the course.
     */
    const onCdpLinkClick = (courseCode, status) => {
        let additionalData = {
            cCode: courseCode,
            status: status
        }
        analyticsStore.addEvent(`RegPrev-CdpLinkClick-${status}`,{}, additionalData);
    };

    // Determine if course selector and section entry code fields should be present for the course
    // Issues never show inputs; If part of a cluster, only show inputs if course is the parent of the block
    let hasInputs = !issue && (type === PlanItemType.planned || type === PlanItemType.backup);
    if ( isClustered && hasInputs) {
        hasInputs = isClusterParent;
    }

    const courseKey = `course-item-${course.planItemDataObject.id}`

    // Build course heading and CDP link for UI display
    let courseCode = normalize(course.courseDetails.code);
    let Heading = courseTitleHeadings ?? "h3";
    let a11yCourseCode = course.courseDetails.hasOwnProperty("curriculumTitle") ? `${course.courseDetails.curriculumTitle} ${course.courseDetails.courseNumber}`: courseCode;
    let a11yCourseTitle = `${a11yCourseCode} ${course.courseDetails.courseTitle}`;

    // Get store state for course selection (form data is url persisted) and create checkbox with value defaulted to state value
    let sendToRegistration = registrationDetailsStore.getRegistrationItemValue('sendToRegistration', course.planItemDataObject.id);

    // Check to see if there are any course level issues
    let courseIssues = null;
    let analyticsData = null;

    //Check if there are too many primary section added issues. This is being done since the map of issues from the
    // back end does not guarantee order and we need the primary section issues to be displayed before the secondary
    // section issues
    let primaryMsgCode = [RegistrationConstants.PrimarySectionActionCodes.REMOVE_ALL_BUT_ONE_SECTION,
        RegistrationConstants.PrimarySectionActionCodes.REMOVE_ALL_BUT_ONE_REQUIRED_SECTION,
        RegistrationConstants.PrimarySectionActionCodes.CHECK_WITH_DEPARTMENT];

    if ( issue && course.validationIssues && !isEmpty(course.validationIssues)) {
        analyticsData = {
            hasIssue: `${issue}`,
            hasSections: `${course.planActivities.length > 0}`,
            timeSchPublished: `${isPublished}`,
            cCode: courseCode,
            planItemType: 'course'
        };
        let courseIssuesList = [];
        for ( let actionCode of primaryMsgCode) {
            if(course.validationIssues[actionCode] !== undefined) {
                courseIssuesList.push(
                    _makeMessageComponent(actionCode, course, analyticsData)
                );
                course.validationIssues[actionCode] = undefined;
            }
        }

        for (const errorCode in course.validationIssues) {
            if (course.validationIssues[errorCode] !== undefined){
                courseIssuesList.push(
                    _makeMessageComponent(errorCode, course, analyticsData)
                )
            }
        }

        courseIssues =
            <div className="mt-2 d-flex align-items-start gap-2" id={`${courseKey}-messages`}>
                <div><FaExclamationTriangle className="text-warning" title={"Warning"} /></div>
                <div>
                    {courseIssuesList}
                </div>
            </div>;
    }

    let courseTitle =
        <div className="flex-grow-1">
            <Heading className="mb-0 d-inline align-middle h3">
                <a onClick={(e) => onCdpLinkClick(courseCode, issue ? "issues" : "ready", e)}
                   aria-label={a11yCourseTitle}
                   {...(courseIssues !== null ? {"aria-describedby": `${courseKey}-messages`} : {})}
                   href={`/course/#/courses/${courseCode}?id=${course.courseDetails.courseId}`}>
                    {courseCode}
                </a>
            </Heading>
            <span className="sr-only">{course.courseDetails.credit} {pluralize("Credit", course.courseDetails.credit)}</span>
            <Badge bg="light-gray"
                   title={`${course.courseDetails.credit} ${pluralize("Credit", course.courseDetails.credit)}`}
                   className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2"
                   aria-hidden="true">
                {course.courseDetails.credit} <abbr title="Credit">CR</abbr>
            </Badge>
            <a onClick={(e) => onCdpLinkClick(courseCode, issue ? "issues" : "ready", e)}
               aria-hidden={true}
               className="d-block lead me-4"
               title={`${courseCode} - ${course.courseDetails.courseTitle}`}
               href={`/course/#/courses/${courseCode}?id=${course.courseDetails.courseId}`}>
                {course.courseDetails.courseTitle}
            </a>
        </div>;

    // If there are activities associated with the course, build a table of those activities
    let activities = null;
    if ( course.planActivities && course.planActivities.length ) {
        let activitiesList = course.planActivities.map((activity, activityIndex) => {
            const activityKey = `activity-item-${activity.activityId.replaceAll(':', '-')}-${activityIndex}`;
            const a11ySectionCode = activity.code;
            // Check to see if there are any activity level issues
            let activityIssues;
            let analyticsData = null;
            const mode = activity.onlineLearningText ? activity.onlineLearningText : "In-person";
            if ( issue && activity.validationIssues && !isEmpty(activity.validationIssues) ) {
                analyticsData = {
                    hasIssue: `${issue}`,
                    timeSchPublished: `${isPublished}`,
                    cCode: courseCode,
                    sCode: activity.code,
                    planItemType: 'section'
                };
                let activityIssuesList = [];
                for (const errorCode in activity.validationIssues) {
                    activityIssuesList.push(
                        <div key={errorCode}>
                            <Lists className={_isNowBetweenP1RegAndLastAddDate() ? "text-danger" :""} data={activity.validationIssues[errorCode]} unstyled />
                            {errorMessageFactory.generateLink(errorCode, course, analyticsData)}
                        </div>
                    )
                }

                activityIssues =
                    <div className="mt-2 d-flex align-items-start gap-2">
                        <div><FaExclamationTriangle className="text-warning" title={"Warning"} /></div>
                        <div>
                            {activityIssuesList}
                        </div>
                    </div>;
            }

            // If ready list of courses, build form element for entry code input with value defaulted to store state value (form data is url persisted)
            let entryCode = null;
            let enrollmentRestrictions;
            let restrictions = [];
            let restriction;
            let message;
            if ( hasInputs ) {
                if ( ( activity.enrollRestriction || activity.addCodeRequired || activity.independentStudy) && activity.stateKey === 'active') {
                    if (appStore.isAuthenticated() && !appStore.isSocialUser()) {
                        restriction = <a
                            href={'https://sdb.admin.washington.edu/timeschd/uwnetid/sln.asp?QTRYR=' + activity.qtryr + '&SLN=' + activity.registrationCode}
                            onClick={() => handleClick(activity.qtryr, activity.activityId, activity.registrationCode)}
                            target="_blank">
                            View Enrollment Restrictions
                        </a>
                    }
                    // Sections with enrollment restrictions, auth users get a link to TS, unauth get messaging
                    if (activity.enrollRestriction) {
                        message = "This course section has enrollment restrictions. "

                    }
                    if (activity.independentStudy) {
                        message = "This course section requires a Faculty Code. "
                    }
                    if (activity.addCodeRequired && !activity.independentStudy) {
                        message = "This course section requires an Add Code or a Faculty Code. "
                    }
                    if ( activity.enrollRestriction && ( activity.addCodeRequired || activity.independentStudy ) ) {
                        message = "This course section requires an Add Code or a Faculty Code and has enrollment restictions. "
                    }

                    restrictions.push(
                        <div key={`${activity.id}-restrictions`}>
                            {message}
                            {restriction}
                        </div>
                    );
                }
                let entryCodeId = activity.activityId.replaceAll(':', '-');
                let isEntryCodeRequired = ( activity.independentStudy || activity.addCodeRequired || _isNowAfterEighthDay() );
                let entryCodeLabel = (activity.independentStudy ? "Faculty Code" : "Add Code") + (isEntryCodeRequired ? " (required)":"");

                let entryCodeControl =
                    <Form.Control type="number"
                                  aria-label={`${entryCodeLabel} for ${a11yCourseCode} ${a11ySectionCode}`}
                                  aria-required={isEntryCodeRequired}
                                  value={activity.entryCode}
                                  size="sm"
                                  style={{width: '6rem'}}
                                  data-activity-id={activity.activityId}
                                  disabled={isFalse(sendToRegistration) || ! appStore.isStudent()}
                                  autoComplete="off"
                                  autoCorrect="off"
                                  autoCapitalize="off"
                                  spellCheck="false"
                                  maxLength={5}
                                  pattern="[0-9]*"
                                  onChange={onChange}
                                  onInput={handleInputMaxLength}
                                  onKeyDown={handleInputNumbersOnly}
                                  onWheel={(e) => e.target.blur()}
                    />;

                let entryCodeComponent;
                if (isEntryCodeRequired) {
                    entryCodeComponent =
                        <>
                            <Form.Label  className="mb-0 mb-sm-1">
                                {entryCodeLabel}
                            </Form.Label>
                            {entryCodeControl}
                        </>
                } else {
                    const EntryCodeToggle = ({children, eventKey}) => {
                        const decoratedOnClick = useAccordionButton(eventKey, () => {});

                        return (
                            <Button variant="inline-link"
                                    title={`Click to input ${children}`}
                                    className="mb-0 mb-sm-1"
                                    onClick={decoratedOnClick}>
                                {children}
                            </Button>
                        )

                    };

                    entryCodeComponent =
                        <Accordion>
                            <EntryCodeToggle eventKey={activity.registrationCode}>
                                {entryCodeLabel}
                            </EntryCodeToggle>
                            <Accordion.Collapse eventKey={activity.registrationCode}>
                                {entryCodeControl}
                            </Accordion.Collapse>
                        </Accordion>;
                }

                entryCode =
                    <Col xs={12} sm={4} className={classnames({"d-none": isFalse(appStore.isStudent() || appStore.isAdviser())})}>
                        <Form.Group controlId={`entry-code-${entryCodeId}`} size="sm" className="mt-2 mt-sm-0 mb-0 mb-sm-3">
                            {entryCodeComponent}
                        </Form.Group>
                    </Col>

                //If new reg exp is enabled do not ask for add code
                if (isTrue(appStore.getAppAttribute("enable_new_reg_exp"))) {
                    entryCode = null;
                }
            }
            if (restrictions.length > 0) {
                enrollmentRestrictions =
                    <div className="mt-2 d-flex align-items-start gap-2">
                        <div><FaExclamationTriangle className="text-warning" title={"Warning"} /></div>
                        <div>
                            {restrictions}
                        </div>
                    </div>;
            }

                // If summer term, create a badge for display
            let summerBadge = null;
            if ( activity.hasOwnProperty("summerTerm") && activity.summerTerm ) {
                summerBadge =
                    <>
                        <span className="sr-only">Summer Session:</span>
                        <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">{activity.summerTerm}</Badge>
                    </>
            }

            // If section is active, show current enrollment status/count/maximum, else show badge of section state (withdrawn/suspended)
            let enrollment;
            if ( activity.stateKey === 'active' && !isEmpty(activity.enrollStatus)) {
                let enrollStatus = null;
                const enrollStatusValue = activity.enrollStatus.toLowerCase();

                if ( enrollStatusValue === 'open' || enrollStatusValue === 'closed' ) {
                    let enrollBadgeVariant;
                    switch (enrollStatusValue) {
                        case "closed":
                            enrollBadgeVariant = "danger";
                            break;
                        case "open":
                            enrollBadgeVariant = "success";
                            break;
                        default:
                            enrollBadgeVariant = "white";
                    }

                    enrollStatus =
                        <Badge bg={enrollBadgeVariant} className="fs-9 text-uppercase">
                            <span className="sr-only">Section is</span>
                            {enrollStatusValue}
                        </Badge>;
                }

                // Don't show negative numbers for available seats since sometimes counts are larger than maximums
                let availableSeats = Math.max(0, (activity.enrollMaximum - activity.enrollCount));

                enrollment =
                    <>
                        {enrollStatus}
                        <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border">
                            <span>{availableSeats} </span>
                            <span className="sr-only">seats available </span>
                            <abbr title="available" aria-hidden={true}>avail</abbr>
                            <span> of {activity.enrollMaximum}{activity.enrollEstimate === 'estimate' ? <abbr title="estimated">E</abbr> : ''}</span>
                            <span className="sr-only"> seats</span>
                        </Badge>
                    </>;
            } else {
                enrollment = null;
                if ( activity.stateKey !== 'active' ) {
                    enrollment =
                        <a href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/faqs/" target="_blank">
                            <Badge bg="uw-metallic-gold" className="fs-9 text-uppercase">
                                {activity.stateKey}
                            </Badge>
                        </a>;
                }
            }

            let instructor =
                <Col xs={12} sm={8} className="section-instructor" aria-hidden={isEmpty(activity.instructor)}>
                    Instructor: {isEmpty(activity.instructor) ? "--" : activity.instructor}
                </Col>;

            let sectionAction;
            // If the section is part of a block reg, do not give delete action
            if ( ( isClustered && clusterParent === activity.registrationCode ) || ( ! isClustered ) ) {
                let hoverText = `Remove ${courseCode} ${activity.code} and any planned quizzes, labs, etc. from plan`;
                let ariaLabel = `Remove ${a11yCourseCode} ${a11ySectionCode} and any planned quizzes, labs, etc. from plan`;
                if ( ! activity.primary ) {
                    hoverText = `Remove ${courseCode} ${activity.code} from plan`;
                    ariaLabel = `Remove ${a11yCourseCode} ${a11ySectionCode} from plan`;
                }

                let analyticsData = {
                    hasIssue: `${issue}`,
                    timeSchPublished: `${isPublished}`,
                    cCode: courseCode,
                    sCode: activity.code,
                    planItemType: 'section',
                    context: 'RegView-link'
                };

                // create a new hash with the same properties as the activity
                // with a new field with cluster information
                // and a boolean about whether a primary section is planned by itself
                let modalActivity = { ...activity };
                modalActivity.isClustered = isClustered;
                modalActivity.hasPlannedSecondary = course.planActivities.filter( a => activity.code === a.primaryActivityOfferingCode && !a.primary ).length > 0;

                const onDeleteSection = () => {
                    planItemModalStore.openModal('delete', modalActivity, analyticsData);
                };

                sectionAction =
                    <ButtonGroup className="ms-auto ps-3">
                        <Notify course={course.courseDetails} section={activity} disabled={appStore.isAdviser() || appStore.isSocialUser()} additionalAnalytics={{context: "RegView"}} />
                        <Button variant="icon-link"
                                id={"section-remove-" + activity.registrationCode}
                                title={hoverText}
                                aria-label={ariaLabel}
                                onClick={onDeleteSection}
                                disabled={appStore.isAdviser()}>
                            <FaTimesCircle aria-hidden="true" />
                        </Button>
                    </ButtonGroup>;
            }

            let sectionStatusAnalytics = {
                cCode: normalize(activity.courseCode),
                sCode: activity.code,
                sln: activity.registrationCode,
                term: termId,
                context: 'RegPrev'
            };

            let sectionMessages = null;
            if (activityIssues || enrollmentRestrictions) {
                sectionMessages =
                    <div id={`${activityKey}-messages`}>
                        {activityIssues}
                        {enrollmentRestrictions}
                    </div>
            }

            // return a row of section (activity) data
            return (
                <li key={activityIndex}>
                    <div className="d-flex align-items-start">
                        <div className="sectionCode text-nowrap" style={{width: "47.5px", minWidth: "47.5px"}}>
                            <SectionCode section={activity} additionalAnalytics={{context: "RegView-link"}} includeFileIcon={false}/>
                        </div>
                        <Row className="flex-grow-1 d-flex align-items-center g-0">
                            <Col xs={12} sm={8}>
                                <RegistrationCodeLink
                                    sln={activity.registrationCode}
                                    qtryr={activity.qtryr}
                                    additionalAnalytics={sectionStatusAnalytics}
                                    className="align-middle me-2"
                                    {...(sectionMessages !== null ? {describedby: `${activityKey}-messages`} : {})}
                                />
                                <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border">
                                    <span className="sr-only">Section Type: </span>
                                    <span>{activity.activityOfferingType}</span>
                                </Badge>
                                {summerBadge}
                            </Col>
                            <Col xs={12} sm={4} className="text-sm-end">
                                {enrollment}
                            </Col>
                        </Row>
                        {sectionAction}
                    </div>
                    <div style={{paddingLeft: "47.5px", paddingRight: "95px"}}>
                        <SectionMeetingDetails meetings={activity.meetingDetailsList} mode={mode} campus={activity.campus} />
                        <Row>{instructor}{issue ? null : entryCode}</Row>
                        {sectionMessages}
                    </div>
                </li>
            );
        });

        activities = <ul className="mt-2 list-unstyled list-add-space" aria-label={`Sections for ${a11yCourseCode}`}>{activitiesList}</ul>;
    }

    let courseActions = <></>;
    // If the course part of a block reg and its the parent or if it is a course outside a block reg, give course actions menu options
    if ( ! isClustered ) {
        let analyticsData = {
            hasIssue: `${issue}`,
            hasSections: activities !== null,
            timeSchPublished: `${isPublished}`,
            cCode: courseCode,
            planItemType: 'course',
            context: 'RegView-modal'
        };

        let modalCourse = { ...course };

        // Add some data about block reg to section data
        modalCourse.isClustered = isClustered;

        const onDeleteCourse = () => {
            planItemModalStore.openModal('delete', modalCourse, analyticsData);
        };

        const onMoveCourse = () => {
            planItemModalStore.openModal('move', modalCourse, analyticsData);
        };

        const onAddEditSections = () => {
            planItemModalStore.openModal('update', modalCourse, {context: "RegView-modal"});
        };

        let changeType;
        switch (type) {
            case PlanItemType.planned:
                changeType = PlanItemType.backup;
                break;
            case PlanItemType.backup:
                changeType = PlanItemType.planned;
                break;
        }

        const onChangeType = (type) => {
            let additionalData = {
                cCode: courseCode,
                qtr: termId,
                from: capitalize(type),
                to: capitalize(type),
                context: "RegView-modal"
            }
            const labels = analyticsStore.label(MetricLabels.quarter, termId).add(MetricLabels.appContextName,additionalData.context).get();
            analyticsStore.addEvent( "Change-PlanItem",labels, additionalData);

            planItemStore.changeType(modalCourse.planItemDataObject.id, type, courseCode, modalCourse.note, modalCourse.placeHolderText);
        };

        let dropdownAddEdit;
        if (course.sectionsAvailable) {
            dropdownAddEdit =
                <>
                    <Dropdown.Item disabled={appStore.isAdviser()} onClick={onAddEditSections}>Select Section(s)</Dropdown.Item>
                    <Dropdown.Divider />
                </>
        }

        courseActions =
            <Dropdown id={`course-actions-${courseCode.toLowerCase().replace(/\s/g, '-')}`}
                      align="end" drop="up">
                <Dropdown.Toggle variant="icon"
                                 className="dropdown-no-caret"
                                 aria-label={`More actions for ${a11yCourseCode}`}
                                 title={`More actions for ${courseCode}`}>
                    <FaEllipsisH aria-hidden="true" size="1.25rem"/>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow-sm">
                    {dropdownAddEdit}
                    <Dropdown.Item disabled={appStore.isAdviser()} onClick={() => onChangeType(changeType)}>Mark as <span className="text-capitalize">{changeType}</span></Dropdown.Item>
                    <Dropdown.Item disabled={appStore.isAdviser()} onClick={onMoveCourse}>Move to Another Quarter</Dropdown.Item>
                    <Dropdown.Item disabled={appStore.isAdviser()} onClick={onDeleteCourse}>Delete</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>;
    }

    // return course item with sections
    let courseListItemClasses =
        classnames(
            "border-start",
            {"border-start-5": !isClustered},
            {"border-start-3": isClustered},
            {"border-top": !isClustered},
            "py-3 ps-3 pe-0",
            {"border-start-purple": sendToRegistration && !issue && !isClustered},
            {"border-start-purple-medium": !issue && isClustered && isClusterParentSendToRegistration}
        );
    return (
        <li id={`plan-item-${course.planItemDataObject.id}`} className={courseListItemClasses}>
            <div className="d-flex align-items-center">
                {courseTitle}
                {/*{courseSelector}*/}
                {courseActions}
            </div>
            {courseIssues}
            {activities}
        </li>
    );
});

/**
 * Component for creating a ListGroup of courses along with sections. Can be used for a list of courses that are ready or have issues.
 * @prop courses - a list of courses with data (required)
 * @prop issues - boolean, true if list of courses have issues with registration
 */
class CoursesList extends Component {

    /**
     * Comparison function for course terms, used on summer term courses.
     * Course list should be returned in the following order: Full, A, B
     */
    courseTermComparator(x, y) {
        // Avoid "Cannot read property 'summerTerm' of undefined"
        let xActivity, yActivity = undefined;

        // fully check for undefined at each level, in case we are looking at a past term
        if ( ( x[1] && size(x[1].courses) > 0 && size(x[1].courses[0].planActivities) > 0 ) ) {
            xActivity = x[1].courses[0].planActivities[0];
        }

        if ( ( y[1] && size(y[1].courses) > 0 && size(y[1].courses[0].planActivities) > 0 ) ) {
            yActivity = y[1].courses[0].planActivities[0];
        }

        if ( xActivity && yActivity ) {
            // Check to make sure a summer term is set on items being compared
            if (xActivity.summerTerm && yActivity.summerTerm) {
                let summerTerms = {
                    "full-term": 0,
                    "a-term": 1,
                    "b-term": 2
                };

                // Assumption is made that all plan activities are within the same summer term per BA, so just looking at the first activity within the course
                let xTerm = summerTerms[xActivity.summerTerm.toLowerCase()];
                let yTerm = summerTerms[yActivity.summerTerm.toLowerCase()];

                if (xTerm < yTerm)
                    return -1;
                if (xTerm > yTerm)
                    return 1;
            }
        }

        return 0;
    }

    /**
     * Comparison function for courses within a cluster to make the parent of the cluster the first entry
     * @param parentSln: SLN of block reg parent
     */
    clusterParentComparator(parentSln) {
        return function(a, b) {
            // Check to make sure course has planned activities
            if ( a && a.planActivities.length && b && b.planActivities.length) {
                // Find if the parent SLN exists within the list of course activities
                if ( a.planActivities.some(activity => activity.registrationCode === parentSln) )
                    return -1;
                if ( b.planActivities.some(activity => activity.registrationCode === parentSln) )
                    return 1;
            }

            return 0;
        }
    }

    render() {
        let {issues, courses, ...otherProps} = this.props;
        // Build a model for course containers
        let courseContainers = registrationDetailsStore.getCourseContainers(courses);
        // Sort the containers by the course activity term (only applies to summer courses)
        let courseContainersSorted = [];
        if (!isEmpty(courseContainers)) {
            courseContainersSorted = Object.entries(courseContainers).sort(this.courseTermComparator);
        }
        let courseContainerItems = [];
        // Iterate over containers to create list items
        for (let [key] of courseContainersSorted) {
            if ( courseContainers.hasOwnProperty(key) ) {
                let container = courseContainers[key];
                let courseContainerItem = null;
                // If the container does not contain a block reg, then create a list item for a single course
                if ( isFalse(container.clustered) && container.courses.length === 1 ) {
                    courseContainerItem =
                        <CourseListItem key={key}
                                        course={container.courses[0]}
                                        issue={issues} {...otherProps} />;
                } else if (container.clustered) {
                    // Determine if the SLN of the parent of the block reg is selected for passing off to SPS
                    let sendToRegistration = registrationDetailsStore.getRegistrationItemValue('sendToRegistration', container.clusterParent);

                    // Container is a block reg, create a list of courses contained in the block
                    let clusteredCourses = container.courses.sort(this.clusterParentComparator(container.clusterParent)).map((course, courseIndex) => {
                        return (
                            <CourseListItem key={courseIndex}
                                            course={course}
                                            issue={issues}
                                            clusterParent={container.clusterParent}
                                            isClusterParent={course.planActivities.some(activity => activity.registrationCode === container.clusterParent)} {...otherProps}
                                            isClusterParentSendToRegistration={sendToRegistration}
                                            isClustered />
                        );
                    });

                    let courseContainerTitle = ( container.clusterType === "FIG" ? container.clusterType : "cluster" );

                    let courseContainerIssue = null;
                    if ( issues ) {
                        courseContainerIssue =
                            <div className="text-danger px-3 py-2">You must resolve the issue(s) below in order to successfully register for this entire {courseContainerTitle}.</div>
                    }

                    let clusterClasses =
                        classnames(
                            "border-start",
                            "border-start-2",
                            {"border-start-purple": sendToRegistration && !issues},
                            "border-bottom",
                            "border-bottom-width-2",
                            {"border-bottom-purple": sendToRegistration && !issues}
                        );
                    let clusterTitleClasses =
                        classnames(
                            {"bg-purple": sendToRegistration && !issues},
                            {"bg-border": issues || !sendToRegistration},
                            {"text-white": sendToRegistration && !issues},
                            "text-capitalize fw-bold py-1 px-2"
                        );

                    // Create a list item for the block, which contains a nested list of courses within the block
                    courseContainerItem =
                        <li key={key}>
                            <div className={clusterClasses}>
                                <div className={clusterTitleClasses}>
                                    <FaLink aria-hidden="true"  className="me-1" flip="horizontal" />
                                    {courseContainerTitle}
                                </div>
                                {courseContainerIssue}
                                <ul className="list-unstyled list-add-space">
                                    {clusteredCourses}
                                </ul>
                            </div>
                        </li>;
                }

                if ( courseContainerItem ) {
                    courseContainerItems.push(courseContainerItem);
                }
            }
        }

        let listProps = {};
        if (this.props.describedby) {
            listProps["aria-describedby"] = this.props.describedby
        }

        return (
            <ul className="registrationCoursesList list-unstyled list-add-space mb-0" {...listProps}>
                {courseContainerItems}
            </ul>
        );
    }
}

CoursesList.propTypes = {
    courses: PropTypes.array.isRequired,
    type: PropTypes.string.isRequired,
    issues: PropTypes.bool,
    isPublished: PropTypes.bool,
    termId: PropTypes.string.isRequired,
    describedby: PropTypes.string,
    courseTitleHeadings: PropTypes.oneOf(['h1','h2','h3','h4','h5','h6']),
};

export default observer(CoursesList);